import React, { useMemo } from 'react'
import {
  FormCheckboxField,
  FormField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  LuckyDrawRewardsDto,
  LuckyDrawRewardTypeDto,
} from 'api/Trading-Accounts/Types/MarketingCampaignResultsDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { NumberPattern } from 'utils/formValidation'

export interface RewardDetailsFormValues {
  id?: string
  number: string
  rewardTypeId: string
  topPrize: boolean
  currencyId: string | null
  reward: string
  rewardChanceWeight: string
}

function getNumberOptions(availableNumbers: number[], currentNumber?: string) {
  const set = new Set<number>(availableNumbers)
  const current = Number(currentNumber)
  if (current && !Number.isNaN(current)) set.add(current)
  return Array.from(set).sort((a, b) => a - b)
}

const AddLuckyDrawRewardFormUI: React.FC<FormikProps<RewardDetailsFormValues> & OuterProps> = (
  props
) => {
  const { errors, handleSubmit, isSubmitting, values } = props

  const selectedType = useMemo(
    () => props.rewardTypes.find((rt) => rt.id.toString() === values.rewardTypeId),
    [props.rewardTypes, values.rewardTypeId]
  )
  const showCurrency = (selectedType?.name || '').toLowerCase() === 'cash'
  const isNoPrize = (selectedType?.name || '').toLowerCase() === 'no prize'
  const numberOptions = useMemo(
    () => getNumberOptions(props.availableNumbers, values.number),
    [props.availableNumbers, values.number]
  )

  const handleTopPrizeToggle = async (next: boolean) => {
    if (props.onTopPrizeChangeRequest) {
      const allow = await props.onTopPrizeChangeRequest(next)
      if (allow) await props.setFieldValue('topPrize', next)
      return
    }
    await props.setFieldValue('topPrize', next)
  }

  return (
    <DialogContent size='md' render={<Form />}>
      <DialogHeader>
        <DialogTitle>
          {props.selectedReward ? 'Edit Lucky Draw Reward' : 'Add Lucky Draw Reward'}
        </DialogTitle>
      </DialogHeader>
      <FormSelectField
        name='number'
        label='Reward #'
        value={values.number}
        options={numberOptions.map((number) => ({
          id: number.toString(),
          name: number.toString(),
        }))}
        error={errors.number}
        onChange={(value) => props.setFieldValue('number', value.toString())}
      />
      <FormSelectField
        name='rewardTypeId'
        label='Reward Type'
        value={values.rewardTypeId}
        options={props.rewardTypes.map((rewardType) => ({
          id: rewardType.id.toString(),
          name: rewardType.name,
        }))}
        error={errors.rewardTypeId}
        onChange={(value) => props.setFieldValue('rewardTypeId', value.toString())}
      />
      {showCurrency ? (
        <FormSelectField
          name='currencyId'
          label='Currency'
          value={values.currencyId ?? undefined}
          options={props.currencies.map((currency) => ({
            id: currency,
            name: currency,
          }))}
          error={errors.currencyId ?? undefined}
          onChange={(value) => props.setFieldValue('currencyId', value.toString())}
        />
      ) : (
        <FormReadonlyField label='Currency' value='NA' />
      )}
      <FormField name='reward' label='Reward' disabled={isNoPrize} error={errors.reward} />
      <FormField
        name='rewardChanceWeight'
        label='Reward Chance Weight'
        error={errors.rewardChanceWeight}
      />
      <FormCheckboxField
        name='topPrize'
        label='Top Prize'
        value={values.topPrize}
        onChange={(next) => {
          void handleTopPrizeToggle(next)
        }}
      />
      <DialogFooter>
        <Button key='button' type='button' onClick={() => handleSubmit()} disabled={!!isSubmitting}>
          Submit
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

interface OuterProps {
  onSubmit(values: RewardDetailsFormValues): void
  selectedReward: LuckyDrawRewardsDto | undefined

  currencies: string[]
  defaultCurrency: string
  rewardTypes: LuckyDrawRewardTypeDto[]
  availableNumbers: number[]
  onTopPrizeChangeRequest?: (nextValue: boolean) => Promise<boolean> | boolean
}

function dataToSubmit(values: RewardDetailsFormValues): RewardDetailsFormValues {
  if (values.id) {
    return {
      id: values.id,
      number: values.number,
      rewardTypeId: values.rewardTypeId,
      topPrize: values.topPrize,
      currencyId: values.currencyId,
      reward: values.reward,
      rewardChanceWeight: values.rewardChanceWeight,
    }
  }
  return {
    number: values.number,
    rewardTypeId: values.rewardTypeId,
    topPrize: values.topPrize,
    currencyId: values.currencyId,
    reward: values.reward,
    rewardChanceWeight: values.rewardChanceWeight,
  }
}

export const AddLuckyDrawRewardForm = withFormik<OuterProps, RewardDetailsFormValues>({
  mapPropsToValues: ({ selectedReward, defaultCurrency, rewardTypes, availableNumbers }) => {
    const rewardNumber = selectedReward?.number?.toString() || ''
    const numberOptions = getNumberOptions(availableNumbers, rewardNumber)
    const selectedRewardNumber =
      selectedReward?.number?.toString() ||
      (numberOptions.length > 0 ? numberOptions[0].toString() : '')

    return {
      id: selectedReward?.id || '',
      number: selectedRewardNumber,
      rewardTypeId:
        selectedReward?.rewardType?.id?.toString() || rewardTypes[0]?.id?.toString() || '',
      topPrize: selectedReward?.topPrize || false,
      currencyId: selectedReward?.currency?.id || defaultCurrency,
      reward: (selectedReward?.reward ?? '').toString(),
      rewardChanceWeight: (selectedReward?.rewardChanceWeight ?? '').toString(),
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      const selectedType = props.rewardTypes.find((rt) => rt.id.toString() === values.rewardTypeId)
      const isCash = (selectedType?.name || '').toLowerCase() === 'cash'
      const isNoPrize = (selectedType?.name || '').toLowerCase() === 'no prize'
      const submitValues: RewardDetailsFormValues = {
        ...values,
        currencyId: isCash ? values.currencyId : null,
        reward: isNoPrize ? '0' : values.reward,
      }
      await props.onSubmit(dataToSubmit(submitValues))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const errors: FormikErrors<RewardDetailsFormValues> = {}
    const selectedType = props.rewardTypes.find((rt) => rt.id.toString() === values.rewardTypeId)
    const isCash = (selectedType?.name || '').toLowerCase() === 'cash'
    const isNoPrize = (selectedType?.name || '').toLowerCase() === 'no prize'

    if (Number(values.rewardChanceWeight) > 100 || Number(values.rewardChanceWeight) < 0) {
      errors.rewardChanceWeight = 'Reward chance weight should be between 0 and 100'
    }
    if (!isNoPrize && Number(values.reward) < 0) {
      errors.reward = 'Reward should be a positive number'
    }

    if (!NumberPattern.test(values.rewardChanceWeight)) {
      errors.rewardChanceWeight = 'Reward chance weight should be a number'
    }
    if (!isNoPrize && !NumberPattern.test(values.reward)) {
      errors.reward = 'Reward should be a number'
    }
    if (!values.reward && !isNoPrize) {
      errors.reward = 'Required'
    }
    if (!values.rewardTypeId) {
      errors.rewardTypeId = 'Required'
    }
    if (!values.rewardChanceWeight) {
      errors.rewardChanceWeight = 'Required'
    }
    if (!values.number || !NumberPattern.test(values.number) || Number(values.number) <= 0) {
      errors.number = 'Number should be a positive number'
    }
    if (isCash && !values.currencyId) {
      errors.currencyId = 'Required'
    }

    return errors
  },
  isInitialValid: false,
  enableReinitialize: true,
})(AddLuckyDrawRewardFormUI)
